// ./src/components/features/VehicleCardStats/VehicleCardStats.tsx
import React, { useMemo, useState } from "react";
import {
  Group,
  Stack,
  Text,
  SimpleGrid,
  Tooltip,
  Box,
  SegmentedControl,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { formatCurrency } from "@/utils/formatHelpers";
import {
  getEfficiencyStatus,
  getEfficiencyStatusColor,
  VehicleType,
} from "@/utils/carHelpers";

import {
  DashboardItem,
  CarStats,
  StatsMonetary,
  StatsUnits,
  DashboardAverages,
  CostOfOwnership,
} from "@/utils/api/DashboardService";

import { EfficiencyStatus } from "@/config/efficiencyThresholds";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export type StatsLayout = "grid" | "horizontal";

/** Which period tab is selected */
type StatsPeriod = "current" | "previous" | "average" | "tco";

export interface VehicleCardStatsProps {
  /** Dashboard item with all pre-aggregated, pre-converted stats */
  item: DashboardItem;

  /** Default currency code (used when no monetary data available) */
  currency?: string;

  /** Vehicle type for efficiency status calculation */
  vehicleType?: VehicleType;

  /** Size variant */
  size?: "sm" | "md";

  /** Layout mode: "grid" for desktop, "horizontal" for compact mobile */
  layout?: StatsLayout;

  /** Whether to show efficiency stat (ignored on TCO tab) */
  showEfficiency?: boolean;

  /** Custom class name */
  className?: string;
}

// ============================================
// Constants
// ============================================

const SIZE_CONFIG = {
  sm: {
    iconSize: 18,
    iconStroke: 1.5,
    valueSize: "md" as const,
    unitSize: "xs" as const,
    labelSize: "xs" as const,
    gap: 4,
  },
  md: {
    iconSize: 22,
    iconStroke: 1.5,
    valueSize: "lg" as const,
    unitSize: "sm" as const,
    labelSize: "xs" as const,
    gap: 6,
  },
};

// ============================================
// Unit Translation Helpers
// ============================================

const toCamelCase = (str: string): string => {
  return str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
};

const getDistanceUnitLabel = (
  unit: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string => {
  const key = `common:units.distance.${toCamelCase(unit)}Short`;
  const translated = t(key, { defaultValue: "" });
  return translated || unit;
};

const getVolumeUnitLabel = (
  unit: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string => {
  const key = `common:units.volume.${toCamelCase(unit)}Short`;
  const translated = t(key, { defaultValue: "" });
  return translated || unit;
};

const getConsumptionUnitLabel = (
  unit: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string => {
  const key = `common:units.consumption.${toCamelCase(unit)}Short`;
  const translated = t(key, { defaultValue: "" });
  return translated || unit;
};

// ============================================
// Utility Functions
// ============================================

const getTotalCost = (monetary: StatsMonetary): number => {
  return (
    Number(monetary.refuelsCost || 0) +
    Number(monetary.refuelsTaxes || 0) +
    Number(monetary.expensesCost || 0) +
    Number(monetary.expensesFees || 0) +
    Number(monetary.expensesTaxes || 0)
  );
};

const formatConsumption = (
  consumption: number | null | undefined,
  unit: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): { value: string; unit: string } => {
  if (consumption == null || consumption <= 0) {
    return { value: "—", unit: "" };
  }
  return { value: consumption.toFixed(1), unit: getConsumptionUnitLabel(unit, t) };
};

const formatVolumeValue = (
  volume: number | null | undefined,
  unit: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): { value: string; unit: string } => {
  if (volume == null || volume <= 0) {
    return { value: "0", unit: getVolumeUnitLabel(unit, t) };
  }
  return { value: volume.toFixed(1), unit: getVolumeUnitLabel(unit, t) };
};

const formatAmount = (amount: number): string => {
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// ============================================
// Grid Stat Column (Desktop)
// ============================================

interface GridStatColumnProps {
  iconName: string;
  label: string;
  value: string;
  unit?: string;
  color?: string;
  tooltip?: string;
  size: "sm" | "md";
}

const GridStatColumn: React.FC<GridStatColumnProps> = ({
  iconName,
  label,
  value,
  unit,
  color,
  tooltip,
  size,
}) => {
  const config = SIZE_CONFIG[size];
  const iconColor = color || "var(--mantine-color-dimmed)";

  const inner = (
    <Stack gap={0} align="center" w="100%">
      <Text size={config.labelSize} c="dimmed" ta="center" mb={2}>
        {label}
      </Text>
      <Group gap={6} wrap="nowrap" justify="center" align="center">
        <AppIcon
          iconName={iconName}
          size={config.iconSize}
          stroke={config.iconStroke}
          color={iconColor}
        />
        <Text
          size={config.valueSize}
          fw={600}
          ff="var(--ff-mono)"
          c={color}
          style={{ whiteSpace: "nowrap", lineHeight: 1.2 }}
        >
          {value}
        </Text>
      </Group>
      {unit && (
        <Text size={config.unitSize} c="dimmed" ta="center">
          {unit}
        </Text>
      )}
    </Stack>
  );

  if (tooltip) {
    return (
      <Tooltip label={tooltip} withArrow>
        <Box w="100%">{inner}</Box>
      </Tooltip>
    );
  }

  return inner;
};

// ============================================
// Grid Expenses Column (Desktop)
// ============================================

interface CurrencyCost {
  currency: string;
  totalCost: number;
}

interface GridExpensesColumnProps {
  costs: CurrencyCost[];
  fallbackCurrency: string;
  label: string;
  tooltip: string;
  size: "sm" | "md";
}

const GridExpensesColumn: React.FC<GridExpensesColumnProps> = ({
  costs,
  fallbackCurrency,
  label,
  tooltip,
  size,
}) => {
  const config = SIZE_CONFIG[size];

  const primaryCost = costs.length > 0 ? costs[0] : null;
  const primaryCurrency = primaryCost?.currency || fallbackCurrency;
  const primaryAmount = primaryCost?.totalCost ?? 0;
  const secondaryCosts = costs.length > 1 ? costs.slice(1) : [];

  return (
    <Tooltip label={tooltip} withArrow position="top">
      <Stack gap={0} align="center" w="100%">
        <Text size={config.labelSize} c="dimmed" ta="center" mb={2}>
          {label}
        </Text>
        <Group gap={6} wrap="nowrap" justify="center" align="center">
          <AppIcon
            iconName="Receipt"
            size={config.iconSize}
            stroke={config.iconStroke}
            color="var(--mantine-color-dimmed)"
          />
          <Text
            size={config.valueSize}
            fw={600}
            ff="var(--ff-mono)"
            style={{ whiteSpace: "nowrap", lineHeight: 1.2 }}
          >
            {formatAmount(primaryAmount)}
          </Text>
        </Group>
        <Text size={config.unitSize} c="dimmed" ta="center">
          {primaryCurrency}
        </Text>
        {secondaryCosts.map((c) => (
          <React.Fragment key={c.currency}>
            <Text
              size="xs"
              fw={500}
              ff="var(--ff-mono)"
              c="dimmed"
              ta="center"
              mt={2}
              style={{ whiteSpace: "nowrap" }}
            >
              {formatAmount(c.totalCost)}
            </Text>
            <Text size="xs" c="dimmed" ta="center" style={{ opacity: 0.7 }}>
              {c.currency}
            </Text>
          </React.Fragment>
        ))}
      </Stack>
    </Tooltip>
  );
};

// ============================================
// Horizontal Stat Item (compact mobile)
// ============================================

interface HorizontalStatItemProps {
  iconName: string;
  value: string;
  unit?: string;
  color?: string;
  tooltip?: string;
}

const HorizontalStatItem: React.FC<HorizontalStatItemProps> = ({
  iconName,
  value,
  unit,
  color,
  tooltip,
}) => {
  const theme = useMantineTheme();

  const content = (
    <Group
      gap={4}
      wrap="nowrap"
      style={{ color: color || theme.colors.gray[6] }}
    >
      <AppIcon iconName={iconName} size={14} stroke={1.5} />
      <Stack gap={0}>
        <Text size="sm" fw={600} ff="var(--ff-mono)" style={{ whiteSpace: "nowrap" }}>
          {value}
        </Text>
        {unit && (
          <Text size="xs" fw={500} style={{ opacity: 0.7, whiteSpace: "nowrap" }}>
            {unit}
          </Text>
        )}
      </Stack>
    </Group>
  );

  if (tooltip) {
    return (
      <Tooltip label={tooltip} withArrow position="top">
        <Box>{content}</Box>
      </Tooltip>
    );
  }

  return content;
};

// ============================================
// Horizontal Expenses Display (compact mobile)
// ============================================

interface HorizontalExpensesDisplayProps {
  costs: CurrencyCost[];
  fallbackCurrency: string;
  tooltip: string;
  size: "sm" | "md";
}

const HorizontalExpensesDisplay: React.FC<HorizontalExpensesDisplayProps> = ({
  costs,
  fallbackCurrency,
  tooltip,
  size,
}) => {
  const config = SIZE_CONFIG[size];

  if (costs.length === 0) {
    return (
      <Text size={config.valueSize} fw={600} ff="var(--ff-mono)">
        {formatCurrency(0, fallbackCurrency)}
      </Text>
    );
  }

  return (
    <Tooltip label={tooltip} withArrow position="top">
      <Box>
        <Text
          size={config.valueSize}
          fw={600}
          ff="var(--ff-mono)"
          style={{ whiteSpace: "nowrap" }}
        >
          {formatCurrency(costs[0].totalCost, costs[0].currency)}
        </Text>
        {costs.slice(1).map((c) => (
          <Text
            key={c.currency}
            size="xs"
            fw={500}
            ff="var(--ff-mono)"
            c="dimmed"
            style={{ whiteSpace: "nowrap" }}
          >
            {formatCurrency(c.totalCost, c.currency)}
          </Text>
        ))}
      </Box>
    </Tooltip>
  );
};

// ============================================
// Vertical Separator
// ============================================

const VerticalSeparator: React.FC = () => {
  const theme = useMantineTheme();
  return (
    <Box
      style={{
        width: 1,
        height: 20,
        backgroundColor: theme.colors.gray[3],
        flexShrink: 0,
      }}
    />
  );
};

// ============================================
// Display value shapes
// ============================================

interface MonthlyDisplayValues {
  kind: "monthly";
  costs: CurrencyCost[];
  volumeValue: string;
  volumeUnit: string;
  efficiencyValue: string;
  efficiencyUnit: string;
  efficiencyStatus: EfficiencyStatus | null;
  refuelsCount: number;
  refuelsTooltip: string;
  expensesTooltip: string;
  efficiencyTooltip: string;
}

interface TcoDisplayValues {
  kind: "tco";
  costs: CurrencyCost[];
  perMonthValue: string;
  perMonthUnit: string;
  perDistValue: string;
  perDistUnit: string;
  monthsOwned: number;
  expensesTooltip: string;
}

type PeriodDisplayValues = MonthlyDisplayValues | TcoDisplayValues;

// ============================================
// Display value builders
// ============================================

function getMonthDisplayValues(
  stats: CarStats,
  monetary: StatsMonetary[],
  fallbackCurrency: string,
  vehicleType: VehicleType,
  t: (key: string, opts?: Record<string, unknown>) => string
): MonthlyDisplayValues {
  const costs: CurrencyCost[] = monetary.map((m) => ({
    currency: m.currency || fallbackCurrency,
    totalCost: getTotalCost(m),
  }));

  const units: StatsUnits | undefined = stats.inUserUnits || stats.inCarUnits;
  const volumeUnit = units?.volumeUnit || "l";
  const consumptionUnitStr = units?.consumptionUnit || "l100km";
  const refuelsVolume = units?.refuelsVolume ?? 0;
  const consumption = units?.consumption ?? null;
  const refuelsCount = stats.refuelsCount || 0;

  const { value: volumeValue, unit: volumeUnitLabel } = formatVolumeValue(refuelsVolume, volumeUnit, t);
  const { value: efficiencyValue, unit: efficiencyUnit } = formatConsumption(consumption, consumptionUnitStr, t);
  const efficiencyStatus = getEfficiencyStatus(consumption, vehicleType);

  const expensesTooltip =
    costs.length > 0
      ? costs.map((c) => formatCurrency(c.totalCost, c.currency)).join(" + ")
      : formatCurrency(0, fallbackCurrency);

  return {
    kind: "monthly",
    costs,
    volumeValue,
    volumeUnit: volumeUnitLabel,
    efficiencyValue,
    efficiencyUnit,
    efficiencyStatus,
    refuelsCount,
    refuelsTooltip: t("vehicle.refuelsTooltip", {
      count: refuelsCount,
      volume: `${volumeValue} ${volumeUnitLabel}`,
    }),
    expensesTooltip: t("vehicle.totalExpensesThisMonth", { amount: expensesTooltip }),
    efficiencyTooltip: efficiencyStatus
      ? t("vehicle.fuelEfficiency", { status: efficiencyStatus })
      : t("vehicle.notEnoughData"),
  };
}

function getAverageDisplayValues(
  item: DashboardItem,
  fallbackCurrency: string,
  vehicleType: VehicleType,
  t: (key: string, opts?: Record<string, unknown>) => string
): MonthlyDisplayValues {
  const avg = item.averages;
  const currency = avg?.currency || fallbackCurrency;
  const monthlyExpense = avg?.monthlyExpense ?? 0;
  const costs: CurrencyCost[] = [{ currency, totalCost: monthlyExpense }];

  const units = item.currentMonthStats?.inUserUnits || item.currentMonthStats?.inCarUnits;
  const volumeUnit = units?.volumeUnit || "l";
  const consumptionUnitStr = units?.consumptionUnit || "l100km";
  const monthlyVolume = avg?.monthlyRefuelsVolume ?? 0;
  const monthlyConsumption = avg?.monthlyConsumption ?? null;

  const { value: volumeValue, unit: volumeUnitLabel } = formatVolumeValue(monthlyVolume, volumeUnit, t);
  const { value: efficiencyValue, unit: efficiencyUnit } = formatConsumption(monthlyConsumption, consumptionUnitStr, t);
  const efficiencyStatus = getEfficiencyStatus(monthlyConsumption, vehicleType);

  return {
    kind: "monthly",
    costs,
    volumeValue,
    volumeUnit: volumeUnitLabel,
    efficiencyValue,
    efficiencyUnit,
    efficiencyStatus,
    refuelsCount: 0,
    refuelsTooltip: t("vehicle.avgRefuelsTooltip", {
      volume: `${volumeValue} ${volumeUnitLabel}`,
    }),
    expensesTooltip: t("vehicle.totalExpensesThisMonth", {
      amount: formatCurrency(monthlyExpense, currency),
    }),
    efficiencyTooltip: efficiencyStatus
      ? t("vehicle.fuelEfficiency", { status: efficiencyStatus })
      : t("vehicle.notEnoughData"),
  };
}

function getTcoDisplayValues(
  tco: CostOfOwnership | undefined,
  fallbackCurrency: string,
  distanceUnit: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): TcoDisplayValues {
  const totalCost = tco?.totalCost ?? 0;
  const perMonth = tco?.perMonth ?? null;
  const perDistance = tco?.perDistance ?? null;
  const monthsOwned = tco?.monthsOwned ?? 0;
  const distUnitLabel = getDistanceUnitLabel(distanceUnit, t);

  const perMonthUnit = t("common:stats.perMonth", { defaultValue: "/mo" });
  const perDistUnit = perDistance != null ? `/${distUnitLabel}` : "";

  return {
    kind: "tco",
    costs: [{ currency: fallbackCurrency, totalCost }],
    perMonthValue: perMonth != null ? formatAmount(perMonth) : "—",
    perMonthUnit: perMonth != null ? perMonthUnit : "",
    perDistValue: perDistance != null ? perDistance.toFixed(2) : "—",
    perDistUnit,
    monthsOwned,
    expensesTooltip: t("common:stats.tcoTotal", {
      amount: formatCurrency(totalCost, fallbackCurrency),
      months: monthsOwned,
      defaultValue: "Total cost of ownership: {{amount}} over {{months}} months",
    }),
  };
}

function usePeriodDisplayValues(
  period: StatsPeriod,
  item: DashboardItem,
  fallbackCurrency: string,
  distanceUnit: string,
  vehicleType: VehicleType,
  t: (key: string, opts?: Record<string, unknown>) => string
): PeriodDisplayValues {
  return useMemo(() => {
    if (period === "tco") {
      return getTcoDisplayValues(item.costOfOwnership, fallbackCurrency, distanceUnit, t);
    }

    if (period === "average") {
      return getAverageDisplayValues(item, fallbackCurrency, vehicleType, t);
    }

    const stats =
      period === "current" ? item.currentMonthStats : item.previousMonthStats;
    const monetary =
      period === "current" ? item.currentMonthMonetary : item.previousMonthMonetary;

    return getMonthDisplayValues(stats, monetary, fallbackCurrency, vehicleType, t);
  }, [period, item, fallbackCurrency, distanceUnit, vehicleType, t]);
}

// ============================================
// Period Selector
// ============================================

interface PeriodSelectorProps {
  value: StatsPeriod;
  onChange: (value: StatsPeriod) => void;
  currentMonthStats: CarStats;
  previousMonthStats: CarStats;
  averages: DashboardAverages;
  compact?: boolean;
}

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  value,
  onChange,
  currentMonthStats,
  previousMonthStats,
  averages,
  compact = false,
}) => {
  const { t } = useTranslation(["common"]);

  const currentLabel = useMemo(() => {
    if (currentMonthStats?.month > 0) {
      return t(`common:monthsShort.${currentMonthStats.month}`);
    }
    return t("common:stats.current");
  }, [currentMonthStats, t]);

  const previousLabel = useMemo(() => {
    if (previousMonthStats?.month > 0) {
      return t(`common:monthsShort.${previousMonthStats.month}`);
    }
    return t("common:stats.previous");
  }, [previousMonthStats, t]);

  const avgLabel = useMemo(() => {
    const months = averages?.monthsCount || 0;
    return compact
      ? t("common:stats.avgShort", { count: months })
      : t("common:stats.avg", { count: months });
  }, [averages, compact, t]);

  const data = useMemo(
    () => [
      { label: currentLabel, value: "current" as StatsPeriod },
      { label: previousLabel, value: "previous" as StatsPeriod },
      { label: avgLabel, value: "average" as StatsPeriod },
      { label: t("common:stats.tco", { defaultValue: "TCO" }), value: "tco" as StatsPeriod },
    ],
    [currentLabel, previousLabel, avgLabel, t]
  );

  return (
    <SegmentedControl
      value={value}
      onChange={(val) => onChange(val as StatsPeriod)}
      data={data}
      size="xs"
      fullWidth
      styles={{
        root: {
          backgroundColor: "var(--mantine-color-gray-1)",
        },
      }}
    />
  );
};

// ============================================
// TCO Grid Layout (Desktop)
// ============================================

interface TcoGridProps {
  values: TcoDisplayValues;
  fallbackCurrency: string;
  size: "sm" | "md";
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const TcoGrid: React.FC<TcoGridProps> = ({ values, fallbackCurrency, size, t }) => (
  <SimpleGrid cols={3} spacing="xs">
    <GridExpensesColumn
      costs={values.costs}
      fallbackCurrency={fallbackCurrency}
      label={t("common:stats.total", { defaultValue: "Total cost" })}
      tooltip={values.expensesTooltip}
      size={size}
    />
    <GridStatColumn
      iconName="CalendarStats"
      label={t("common:stats.perMonth", { defaultValue: "/mo" })}
      value={values.perMonthValue}
      unit={values.perMonthUnit}
      size={size}
      tooltip={t("common:stats.tcoPerMonthTooltip", {
        defaultValue: "Average cost per calendar month since ownership start",
      })}
    />
    <GridStatColumn
      iconName="Road"
      label={t("common:stats.perDist", { defaultValue: "Per dist" })}
      value={values.perDistValue}
      unit={values.perDistUnit}
      size={size}
      tooltip={t("common:stats.tcoPerDistTooltip", {
        defaultValue: "Average cost per distance unit driven",
      })}
    />
  </SimpleGrid>
);

// ============================================
// Main Component
// ============================================

export const VehicleCardStats: React.FC<VehicleCardStatsProps> = ({
  item,
  currency = "USD",
  vehicleType = "car",
  size = "md",
  layout = "grid",
  showEfficiency = true,
  className,
}) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const [period, setPeriod] = useState<StatsPeriod>("current");

  // Distance unit — needed for TCO per-distance label
  const distanceUnit =
    item.totalStats?.inUserUnits?.distanceUnit ||
    item.totalStats?.inCarUnits?.distanceUnit ||
    "km";

  const displayValues = usePeriodDisplayValues(
    period, item, currency, distanceUnit, vehicleType, t
  );

  const { currentMonthStats, previousMonthStats, averages } = item;

  // ============================================
  // Horizontal Layout (compact mobile)
  // ============================================

  if (layout === "horizontal") {
    // TCO tab: show total | per-month | per-dist in horizontal layout
    if (displayValues.kind === "tco") {
      return (
        <Stack
          gap={4}
          className={className}
          py={4}
          px={8}
          style={{
            backgroundColor: theme.colors.gray[0],
            borderRadius: theme.radius.sm,
          }}
        >
          <PeriodSelector
            value={period}
            onChange={setPeriod}
            currentMonthStats={currentMonthStats}
            previousMonthStats={previousMonthStats}
            averages={averages}
            compact
          />
          <Group gap="md" justify="space-between" wrap="nowrap">
            <HorizontalExpensesDisplay
              costs={displayValues.costs}
              fallbackCurrency={currency}
              tooltip={displayValues.expensesTooltip}
              size={size}
            />
            <VerticalSeparator />
            <HorizontalStatItem
              iconName="CalendarStats"
              value={displayValues.perMonthValue}
              unit={displayValues.perMonthUnit}
              tooltip={t("common:stats.tcoPerMonthTooltip", {
                defaultValue: "Average cost per calendar month since ownership start",
              })}
            />
            <VerticalSeparator />
            <HorizontalStatItem
              iconName="Road"
              value={displayValues.perDistValue}
              unit={displayValues.perDistUnit}
              tooltip={t("common:stats.tcoPerDistTooltip", {
                defaultValue: "Average cost per distance unit driven",
              })}
            />
          </Group>
        </Stack>
      );
    }

    // Monthly / average tab — original horizontal layout
    const efficiencyColor = getEfficiencyStatusColor(displayValues.efficiencyStatus);
    const efficiencyThemeColor =
      efficiencyColor !== "gray" ? theme.colors[efficiencyColor]?.[6] : undefined;

    return (
      <Stack
        gap={4}
        className={className}
        py={4}
        px={8}
        style={{
          backgroundColor: theme.colors.gray[0],
          borderRadius: theme.radius.sm,
        }}
      >
        <PeriodSelector
          value={period}
          onChange={setPeriod}
          currentMonthStats={currentMonthStats}
          previousMonthStats={previousMonthStats}
          averages={averages}
          compact
        />
        <Group gap="md" justify="space-between" wrap="nowrap">
          <HorizontalExpensesDisplay
            costs={displayValues.costs}
            fallbackCurrency={currency}
            tooltip={displayValues.expensesTooltip}
            size={size}
          />
          <VerticalSeparator />
          <HorizontalStatItem
            iconName="GasStation"
            value={displayValues.volumeValue}
            unit={displayValues.volumeUnit}
            tooltip={displayValues.refuelsTooltip}
          />
          {showEfficiency && <VerticalSeparator />}
          {showEfficiency && (
            <HorizontalStatItem
              iconName="Gauge"
              value={displayValues.efficiencyValue}
              unit={displayValues.efficiencyUnit}
              color={efficiencyThemeColor}
              tooltip={displayValues.efficiencyTooltip}
            />
          )}
        </Group>
      </Stack>
    );
  }

  // ============================================
  // Grid Layout (Desktop)
  // ============================================

  if (displayValues.kind === "tco") {
    return (
      <Stack gap="xs" className={className}>
        <PeriodSelector
          value={period}
          onChange={setPeriod}
          currentMonthStats={currentMonthStats}
          previousMonthStats={previousMonthStats}
          averages={averages}
        />
        <TcoGrid
          values={displayValues}
          fallbackCurrency={currency}
          size={size}
          t={t}
        />
      </Stack>
    );
  }

  // Monthly / average grid
  const efficiencyColor = getEfficiencyStatusColor(displayValues.efficiencyStatus);
  const efficiencyThemeColor =
    efficiencyColor !== "gray" ? theme.colors[efficiencyColor]?.[6] : undefined;

  const columns = showEfficiency ? 3 : 2;

  return (
    <Stack gap="xs" className={className}>
      <PeriodSelector
        value={period}
        onChange={setPeriod}
        currentMonthStats={currentMonthStats}
        previousMonthStats={previousMonthStats}
        averages={averages}
      />
      <SimpleGrid cols={columns} spacing="xs">
        <GridExpensesColumn
          costs={displayValues.costs}
          fallbackCurrency={currency}
          label={
            period === "average"
              ? t("vehicle.avgExpenses")
              : t("vehicle.expenses")
          }
          tooltip={displayValues.expensesTooltip}
          size={size}
        />
        <GridStatColumn
          iconName="GasStation"
          label={
            period === "average"
              ? t("vehicle.avgRefuels")
              : t("vehicle.refuels")
          }
          value={displayValues.volumeValue}
          unit={displayValues.volumeUnit}
          size={size}
          tooltip={displayValues.refuelsTooltip}
        />
        {showEfficiency && (
          <GridStatColumn
            iconName="Gauge"
            label={t("vehicle.efficiency")}
            value={displayValues.efficiencyValue}
            unit={displayValues.efficiencyUnit}
            color={efficiencyThemeColor}
            size={size}
            tooltip={displayValues.efficiencyTooltip}
          />
        )}
      </SimpleGrid>
    </Stack>
  );
};